import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { customerApi, getCustomerToken, setCustomerToken } from '../lib/customerApi.js'

const CustomerContext = createContext(null)

export function CustomerProvider({ children }) {
  const [customer, setCustomer] = useState(null)
  const [impersonatedBy, setImpersonatedBy] = useState(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    if (!getCustomerToken()) {
      setCustomer(null)
      setImpersonatedBy(null)
      setLoading(false)
      return
    }
    try {
      const { customer: me, impersonatedBy: impBy } = await customerApi.me()
      setCustomer(me)
      setImpersonatedBy(impBy ?? null)
    } catch {
      setCustomer(null)
      setImpersonatedBy(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const login = useCallback(async (email, password) => {
    const { token, customer: me } = await customerApi.login(email, password)
    setCustomerToken(token)
    setCustomer(me)
    setImpersonatedBy(null)
    return me
  }, [])

  const register = useCallback(async (email, password, firstName, lastName, marketingOptIn, acceptedTerms) => {
    const { token, customer: me } = await customerApi.register(email, password, firstName, lastName, marketingOptIn, acceptedTerms)
    setCustomerToken(token)
    setCustomer(me)
    setImpersonatedBy(null)
    return me
  }, [])

  const logout = useCallback(async () => {
    try { await customerApi.logout() } finally {
      setCustomerToken(null)
      setCustomer(null)
      setImpersonatedBy(null)
    }
  }, [])

  // Adopts a session returned by a token-confirm flow (email verify, magic
  // link, password reset) without a separate login call — mirrors Prod's
  // useCustomer().adoptSession(token, customer) contract.
  const adoptSession = useCallback((token, me) => {
    setCustomerToken(token)
    setCustomer(me)
    setImpersonatedBy(null)
  }, [])

  const value = useMemo(
    () => ({ customer, impersonatedBy, loading, isAuthenticated: Boolean(customer), login, register, logout, refresh, adoptSession }),
    [customer, impersonatedBy, loading, login, register, logout, refresh, adoptSession],
  )

  return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>
}

export function useCustomer() {
  const ctx = useContext(CustomerContext)
  if (!ctx) throw new Error('useCustomer must be used within CustomerProvider')
  return ctx
}
